import { videos } from '@/mocks/videos';
import type { Video, VideoCategory } from '@/types/video';

export interface VideoFilter {
  region?: string;
  year?: string;
  sort?: 'update' | 'hot';
}

const sortByHot = (a: Video, b: Video) => b.views - a.views;
const sortByUpdate = (a: Video, b: Video) =>
  new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();

export const getAllVideos = (): Video[] => videos;

export const getVideoById = (id: string): Video | undefined =>
  videos.find((item) => item.id === id);

export const getVideosByCategory = (
  category: VideoCategory,
  filter: VideoFilter = {}
): Video[] => {
  const { region = '全部', year = '全部', sort = 'update' } = filter;
  let list = videos.filter((item) => item.category === category);
  if (region && region !== '全部') {
    list = list.filter((item) => item.region === region);
  }
  if (year && year !== '全部') {
    list = list.filter((item) => String(item.year) === year);
  }
  list = [...list].sort(sort === 'hot' ? sortByHot : sortByUpdate);
  return list;
};

export const getLatestVideos = (count: number): Video[] =>
  [...videos].sort(sortByUpdate).slice(0, count);

export const getHotVideos = (count: number): Video[] =>
  [...videos].sort(sortByHot).slice(0, count);

export const getFeaturedVideos = (count: number): Video[] =>
  [...videos].sort(sortByHot).slice(0, count);

export const searchVideos = (keyword: string): Video[] => {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return videos.filter((item) => {
    const haystack = `${item.title} ${item.description} ${item.tags.join(' ')} ${item.region}`;
    return haystack.toLowerCase().includes(q);
  });
};

export const getRelatedVideos = (video: Video, count = 6): Video[] => {
  const sameCategory = videos
    .filter((item) => item.category === video.category && item.id !== video.id)
    .sort(sortByHot);
  if (sameCategory.length >= count) return sameCategory.slice(0, count);
  const others = videos
    .filter((item) => item.category !== video.category && item.id !== video.id)
    .sort(sortByHot);
  return [...sameCategory, ...others].slice(0, count);
};

export const formatViews = (views: number): string => {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
  return String(views);
};

export const formatDate = (date: string): string => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};